import React from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useWorkspace } from '../contexts/WorkspaceContext';
interface LayoutProps {
  children: React.ReactNode;
}
/**
 * Convert a hex color to HSL components for generating a full palette.
 */
function hexToHsl(hex: string): {
  h: number;
  s: number;
  l: number;
} | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}
/**
 * Generate a full Tailwind-style color palette from a single hex color.
 * Maps the input color to the 600 shade and derives the rest.
 */
function generatePalette(hex: string): Record<string, string> {
  const hsl = hexToHsl(hex);
  if (!hsl) return {};
  const {
    h,
    s
  } = hsl;
  const shades: Record<string, number> = {
    '50': 97,
    '100': 94,
    '200': 86,
    '300': 76,
    '400': 62,
    '500': 50,
    '600': 42,
    '700': 34,
    '800': 26,
    '900': 20,
    '950': 14
  };
  const palette: Record<string, string> = {};
  for (const [shade, lightness] of Object.entries(shades)) {
    const satAdj = lightness > 90 ? Math.max(s - 20, 10) : lightness < 25 ? Math.max(s - 10, 15) : s;
    palette[shade] = `hsl(${h}, ${satAdj}%, ${lightness}%)`;
  }
  return palette;
}
export function Layout({
  children
}: LayoutProps) {
  const {
    currentWorkspace,
    scopeKey
  } = useWorkspace();
  const primaryColor = currentWorkspace.primaryColor || '#2563eb';
  const palette = generatePalette(primaryColor);
  const cssVars: Record<string, string> = {
    '--primary': primaryColor
  };
  for (const [shade, color] of Object.entries(palette)) {
    cssVars[`--primary-${shade}`] = color;
  }
  return <div className="h-screen bg-gray-50 overflow-hidden" style={cssVars as React.CSSProperties} data-id="element-1976" data-mp="24jy7yf628skz">
      {/* Sidebar is position:fixed, overlays when expanded */}
      <Sidebar data-id="element-1977" data-mp="1bvu0bo3xi5fn" />

      {/* Main content offset by the collapsed rail width (62px) */}
      <div className="flex flex-col h-screen overflow-hidden" style={{
      marginLeft: 62
    }} data-id="element-1978" data-mp="1qkgrcn2x4lmp">
        <Header data-id="element-1979" data-mp="1lrdti2r3yq0p" />
        <main className="flex-1 overflow-y-auto" data-id="element-1980" data-mp="1lqucco9a85nq">
          <div className="p-4 sm:p-6 lg:p-8" key={scopeKey} data-id="element-1981" data-mp="y22uappep3oo">
            <div className="max-w-7xl mx-auto" data-id="element-1982" data-mp="t49m5r4jlzlr">{children}</div>
          </div>
        </main>
      </div>
    </div>;
}